// external
import React, { FC, useEffect, useState } from 'react';
import useService from '../../../../hooks/service.hook';
import { useParams, useSearchParams } from 'react-router-dom';
import useSnackbarHelper from '../../../../hooks/snackbar.hook';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// internal
import ThreadsService from '../../../../services/threads.service';
import GuardianService from '../../../../services/guardian.service';
import '../message-board/message-board.page.css';
import COECreateThreadCardComponent from '../../../../components/coe-create-thread-card/coe-create-thread-card.component';
import { Thread } from '../../../../models/thread.model';
import COEThreadCard from '../../../../components/coe-thread-card/coe-thread-card.component';
import COESkeletonComponent from '../../../../components/general/coe-skeleton/coe-skeleton.component';
import COEViewThread, { MessageEmptyState } from '../../../../components/coe-view-thread/coe-view-thread.component';
import { palette } from '../../../../theme';

interface MessageBoardPageProps {
  isCoach: boolean;
  isGuardian?: boolean;
  team: any;
}

const MessageBoardPage: FC<MessageBoardPageProps> = (props) => {
  const threadsService = useService(ThreadsService);
  const guardianService = useService(GuardianService);
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams();
  const { showSnackbar } = useSnackbarHelper();

  const [loading, setLoading] = useState<boolean>(true);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<number | string | null>(null);
  const [composeOpen, setComposeOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [threadPendingDelete, setThreadPendingDelete] = useState<string | number | null>(null);

  const teamGroups = props.team?.groups || [];
  const isGuardian = Boolean(props.isGuardian);

  useEffect(() => {
    getAllThreads();
  }, [id]);

  const getAllThreads = (options?: {
    preferSelectId?: number | string | null;
    openComposeIfEmpty?: boolean;
    selectFirst?: boolean;
  }) => {
    setLoading(true);
    const fetchThreads = isGuardian ? guardianService.getWardTeamThreads(id as string) : threadsService.getThreads(id);

    fetchThreads
      .then((response) => {
        const data: Thread[] = response.data.data || [];
        setThreads(data);

        if (data.length < 1) {
          setSelectedThreadId(null);
          if (
            options?.openComposeIfEmpty !== false &&
            props.isCoach &&
            props.team.status === 'active' &&
            !props.team.billingRequired
          ) {
            setComposeOpen(true);
          } else {
            setComposeOpen(false);
          }
        } else {
          const paramThreadID = searchParams.get('message');
          const preferred = options?.preferSelectId;
          let nextId: number | string | null = null;

          if (preferred != null && data.find((obj) => String(obj.id) === String(preferred))) {
            nextId = preferred;
          } else if (options?.selectFirst) {
            nextId = data[0].id;
          } else if (paramThreadID && data.find((obj) => String(obj.id) === String(paramThreadID))) {
            nextId = paramThreadID;
          } else {
            nextId = data[0].id;
          }

          setSelectedThreadId(nextId);
          if (nextId != null) {
            setComposeOpen(false);
            setSearchParams(new URLSearchParams({ message: String(nextId) }));
          }
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        showSnackbar('There was an error getting all threads. Please try again later', 'error');
      });
  };

  const softRefreshThreads = () => {
    const fetchThreads = isGuardian ? guardianService.getWardTeamThreads(id as string) : threadsService.getThreads(id);

    fetchThreads
      .then((response) => {
        setThreads(response.data.data || []);
      })
      .catch(() => {
        // non-critical
      });
  };

  const handleSetThread = (threadId: string | number) => {
    setComposeOpen(false);
    setSearchParams(
      new URLSearchParams({
        message: String(threadId),
      })
    );
    setSelectedThreadId(threadId);
  };

  const handleOpenCompose = () => {
    setComposeOpen(true);
    setSelectedThreadId(null);
    setSearchParams(new URLSearchParams());
  };

  const handleCloseCompose = () => {
    setComposeOpen(false);
    if (threads.length > 0) {
      const firstId = threads[0].id;
      setSelectedThreadId(firstId);
      setSearchParams(new URLSearchParams({ message: String(firstId) }));
    } else {
      setSelectedThreadId(null);
    }
  };

  const handleComposeSuccess = () => {
    setComposeOpen(false);
    getAllThreads({ openComposeIfEmpty: false, selectFirst: true });
  };

  const requestDeleteThread = (threadId: string | number) => {
    setThreadPendingDelete(threadId);
  };

  const handleCancelDeleteThread = () => {
    setThreadPendingDelete(null);
  };

  const handleConfirmDeleteThread = () => {
    if (threadPendingDelete == null) return;
    const threadId = threadPendingDelete;
    setThreadPendingDelete(null);
    setLoading(true);
    threadsService
      .deleteThread(id, threadId)
      .then(() => {
        setLoading(false);
        getAllThreads({ openComposeIfEmpty: false });
      })
      .catch(() => {
        setLoading(false);
        showSnackbar('There was an error deleting your thread. Please try again later', 'error');
      });
  };

  const filteredThreads = threads.filter((thread) => {
    const q = searchQuery.toLowerCase();
    return (thread.title ?? '').toLowerCase().includes(q) || (thread.created_by ?? '').toLowerCase().includes(q);
  });

  const renderThreads = () => {
    if (filteredThreads.length === 0) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 50 }}>
          <Typography style={{ color: palette.text.secondary }}>
            {threads.length === 0 ? 'No messages yet' : 'No results found'}
          </Typography>
        </div>
      );
    }
    return (
      <List sx={{ p: 0 }}>
        {filteredThreads.map((thread) => (
          <ListItem key={thread.id} sx={{ p: 0, display: 'block' }}>
            <COEThreadCard
              date={thread.created_at}
              creator={thread.created_by}
              creatorProfileImage={thread.creator_profile_image}
              id={thread.id}
              title={thread.title}
              message={thread.message}
              onThreadClicked={() => handleSetThread(thread.id)}
              onDeleteThread={() => requestDeleteThread(thread.id)}
              viewThread={false}
              isCoach={props.isCoach}
              read={thread.read as boolean}
              isSelected={!composeOpen && String(thread.id) === String(selectedThreadId)}
              audience={thread.audience}
              groups={teamGroups}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <div className="message-board-container">
      <div className="message-board-split">
        {/* Left: Inbox */}
        <div className="message-board-inbox">
          <div className="message-board-inbox-header">
            <Typography style={{ fontSize: 16, fontWeight: 700, color: palette.text.primary }}>Inbox</Typography>
            {props.isCoach && props.team.status === 'active' && !props.team.billingRequired && (
              <Button
                onClick={handleOpenCompose}
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 12,
                  px: 1.75,
                  py: 0.75,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                + Compose
              </Button>
            )}
          </div>

          <div style={{ padding: '0 12px 10px 12px', flexShrink: 0 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: palette.background.default,
                  fontSize: 14,
                },
              }}
            />
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[...Array(5)].map((_, i) => (
                  <COESkeletonComponent key={i} />
                ))}
              </div>
            ) : (
              renderThreads()
            )}
          </div>
        </div>

        {/* Right: Detail / Compose */}
        <div className="message-board-pane">
          {composeOpen ? (
            <COECreateThreadCardComponent
              open={composeOpen}
              onClose={handleCloseCompose}
              refreshThreads={handleComposeSuccess}
              teamName={props.team.name}
              groups={teamGroups}
            />
          ) : selectedThreadId ? (
            <COEViewThread
              threadId={selectedThreadId}
              onDeleteThread={(threadId: any) => requestDeleteThread(threadId)}
              refreshThreads={() => getAllThreads({ preferSelectId: selectedThreadId, openComposeIfEmpty: false })}
              softRefreshThreads={softRefreshThreads}
              isCoach={props.isCoach}
              isGuardian={isGuardian}
              teamName={props.team.name}
              groups={teamGroups}
            />
          ) : (
            <MessageEmptyState />
          )}
        </div>
      </div>

      <Dialog open={threadPendingDelete != null} onClose={handleCancelDeleteThread}>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this message thread? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteThread}>Cancel</Button>
          <Button onClick={handleConfirmDeleteThread} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MessageBoardPage;
